import {RequestState, ServerResponseTypes} from "./types";
import {WsServerConnection} from "./wsServerConnection";


export const makeApiServerRequest = (
    wsServerConnection: WsServerConnection,
    urlPath: string,
    bodyJSON?: Object
) => {
    wsServerConnection?.sendMsg({
        p: urlPath,
        ...bodyJSON,
    });
}

const REQUEST_TIMEOUT = 30000;

/* global conts */
export const requestLockMap = new Map<string, RequestState>();


export const getFullRequestKey = (action: string, dataKey: string) => {
    return `${action};${dataKey}`;
}

export const httpLikeRequestTroughWebsocket = <T extends keyof ServerResponseTypes>(
    wsServerConnection: WsServerConnection,
    urlPath: string,
    action: T,
    bodyJSON: Object,
    getUniqueRequestHash: (msg: any) => string,
    abortController?: AbortController,
    CUSTOM_TIMEOUT = REQUEST_TIMEOUT,
): Promise<ServerResponseTypes[T]> => {
    const uniqueRequestId = getFullRequestKey(action, getUniqueRequestHash(bodyJSON));
    let _resolve: ((value: ServerResponseTypes[T]) => void) | undefined;
    let _reject: ((value: unknown) => void) | undefined;
    let _timeout: ReturnType<typeof setTimeout> | undefined;
    const abortSignal = abortController?.signal;
    let onAbort: (() => void) | undefined;
    let shouldRegister = true;

    const cleanup = () => {
        if (_timeout !== undefined) {
            clearTimeout(_timeout);
            _timeout = undefined;
        }
        requestLockMap.delete(uniqueRequestId);
        if (abortSignal && onAbort) {
            abortSignal.removeEventListener('abort', onAbort);
            onAbort = undefined;
        }
    };

    const requestPromise: Promise<ServerResponseTypes[T]> = new Promise((resolve, reject) => {
        const resolveWithCleanup = (value: ServerResponseTypes[T]) => {
            cleanup();
            resolve(value);
        };

        const rejectWithCleanup = (reason: unknown) => {
            cleanup();
            reject(reason);
        };

        _resolve = resolveWithCleanup;
        _reject = rejectWithCleanup;

        const startTimeout = () => {
            _timeout = setTimeout(() => {
                if (abortSignal?.aborted) {
                    return;
                }
                rejectWithCleanup({
                    name: 'Timeout',
                    message: 'timeout',
                    action: action,
                });
            }, CUSTOM_TIMEOUT);
        };

        if (abortSignal?.aborted) {
            shouldRegister = false;
            rejectWithCleanup({
                name: 'AbortError',
            });
            return;
        }

        if (requestLockMap.has(uniqueRequestId)) {
            const previousRequest = requestLockMap.get(uniqueRequestId)!;
            clearTimeout(previousRequest.timeout);
            previousRequest.reject({
                name: 'AbortError',
                message: 'aborted by newer call',
            });

            startTimeout();
            return;
        }

        try {
            makeApiServerRequest(wsServerConnection, urlPath, {
                a: action,
                ...bodyJSON,
            });
        } catch (error) {
            shouldRegister = false;
            rejectWithCleanup(error);
            return;
        }

        startTimeout();
    });

    if (abortSignal) {
        if (abortSignal.aborted) {
            shouldRegister = false;
            _reject?.({
                name: 'AbortError',
            });
        } else {
            onAbort = () => {
                _reject?.({
                    name: 'AbortError',
                });
            };
            abortSignal.addEventListener('abort', onAbort);
        }
    }

    if (shouldRegister) {
        requestLockMap.set(uniqueRequestId, {
            promise: requestPromise,
            abortController: abortController,
            getUniqueRequestHash: getUniqueRequestHash,
            reject: _reject!,
            resolve: _resolve!,
            timeout: _timeout!,
        });
    }

    return requestPromise;
}


export const handleHttpLikeRequestTroughWebsocketResponse = (msg: any, requestKey: string, requestState: RequestState) => {
    clearTimeout(requestState.timeout);
    requestLockMap.delete(requestKey);

    if (requestState?.abortController?.signal.aborted) {
        return;
    }

    if ('error' in msg || (typeof msg.s === 'number' && msg.s >= 400)) {
        requestState.reject({
            ...msg,
            message: msg.error || msg.msg || `Request failed with status ${msg.s}`,
            status: msg.status ? msg.status : msg.s,
        });
    } else {
        requestState.resolve(msg);
    }
}
